import React, { Component, Fragment } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { sharpPosition, flatPosition, getAccidental, aspectRatio as ar, pianoParser, getSolfaName } from "../../../../../common/parameters";
import { getScrollBarWidth } from "../../../../../common/helpers/getScrollBarWidth";
import { IDoPos, IMelodyTableData, IParsedDo, IPitch, IScore, TonesetText } from "../../../../../common/types";
import getDoPosition from "../../../../../common/helpers/getDoPosition";
import getDo from "../../../../../common/helpers/getDo";

import voices from "../../../../voices";
import player from "../../../../player";
import piano from "../../../../piano";
import Icon from "../../../../icon";

import { parseDo } from "../../../Score/methods/changeDo";
import Draggable from "../../../Draggable";
import ColorsProvider from "../../../ColorsProvider";

import Wrapper from "../../Wrapper";

import getBirdsPath from "../Common/getBirdsPath";
import MessageDisplay from "../Common/MessageDisplay";
import NotEdited from "../Common/NotEdited";
import getScore from "../Common/getScore";
import getTempo from "../Common/getTempo";
import getKeyOffset from "../Common/getKeyOffset";
import { SubmitButton, PlayBtn, Label } from "../common-style";

import ClefViolin from "../MelodyTable/clef-violin";
import ClefBass from "../MelodyTable/clef-bass";
import { clefOffset } from "../MelodyTable/MelodyTableBar";
import {
    ScrollWrap,
    Scroll,
    Wrap,
    StaffLines,
    StaffLine,
    System,
    Clef,
    Accidental,
    Accidentals,
    OctaveMark,
} from "../MelodyTable/styles";
import { loadPiano, parseScore, scrollTo } from "../MelodyTable/utils";
import { IBarAccidental, IMelodyTableNote } from "../MelodyTable/MelodyTableBar/interfaces";
import { IBundle } from "../MelodyTable/interfaces";
import { isNearAnyDrop } from "../RhytSorting/utils";
import messages from "./messages";
import { IPosition, IProps, IState } from "./interfaces";
import { getRandomDragPositions, Text } from "./utils";
import { DragHolder, Drag, Drop, Ledger, Modifier, Asset1Wrapper, DoClef, ClefLedger, ClefLedgerHolder } from "./styles";
import { getAccidentals } from "../../../Score/score-display/utils";
import { convertToNewColors } from "../../../../utils/convertToNewColors";

const Clock = require("waaclock");

const clefWidth: { [prop: string]: number } = {
    treble: 256.666,
    bass: 314.3,
    "treble-octave": 256.666,
    doClef: 184 * 1.1,
};

const ledgerOffset = {
    treble: 0,
    bass: -12,
    "treble-octave": -7,
}

const spaceDim = 0.9;
const modifierDim = 0.8;

const clefCorrection = {
    treble: 0,
    bass: 4,
};

const clefTop = {
    treble: -4,
    bass: -2,
};

class TonesetStaff extends Component<IProps, IState> {
    static defaultProps = {
        handlePlayCount: () => { },
        handleFailCount: () => { },
    };
    unmounting = false;
    loaded = false;
    clock: any = undefined;
    scroll: HTMLDivElement | null = null;
    wrap: HTMLDivElement | null = null;
    playScrollAnimation?: ReturnType<typeof window.requestAnimationFrame>;
    timer?: ReturnType<typeof setTimeout>;
    frame?: HTMLDivElement;
    scrollBarWidth?: number;
    initialDragPositions?: IPosition[];
    lastSize?: IPosition;
    constructor(props: IProps) {
        super(props);
        this.state = {
            submitable: false,
            submited: false,
            playing: false,
            completed: false,
            playingProps: {},
            bars: false,
            scale: [],
            width: 0,
            doPos: { value: -6, alt: 0 },
            tonesetSolutions: [],
            incorrects: [],
            drags: [],
            frameScale: 1,
            transition: false,
            zIndexes: [],
            playable: false,
        };
        if (process.env.WEBPACK) {
            this.scrollBarWidth = getScrollBarWidth()
        }
        if (props.setReset) {
            props.setReset(this.resetTask);
        }
        if (props.saveState) {
            props.saveState(this.saveState);
        }
        if (props.restoreState) {
            props.restoreState(this.restoreState);
        }
        if (props.showSolution) {
            props.showSolution(this.showSolution);
        }
    }
    resetTask = () => {
        this.setUp();
        this.setState({
            submitable: false,
            submited: false,
            completed: false,
        });
    };
    saveState = () => {
        return {
            submited: this.state.submited,
            incorrects: this.state.incorrects,
            submitable: this.state.submitable,
            completed: this.state.completed,
            drags: this.state.drags,
            tonesetSolutions: this.state.tonesetSolutions,
        } as Partial<IState>;
    };
    restoreState = (state: Partial<IState>) => {
        this.setState({
            submited: state.submited!,
            incorrects: state.incorrects!,
            submitable: state.submitable!,
            completed: state.completed!,
            drags: state.drags!,
            tonesetSolutions: state.tonesetSolutions!,
        });
    };
    showSolution = () => {
        const { drags } = this.state;
        const dropParams = this.getDropParams();
        this.setState({
            drags: drags.map((drag, dragIndex) => {
                return dropParams.drops[dragIndex];
            }),
            completed: true,
            submited: true,
            incorrects: [],
        });
    };
    componentDidMount() {
        this.setUp();
        setTimeout(this.setScale);
        window.addEventListener("resize", this.setScale);
    }

    componentDidUpdate(prevProps: IProps) {
        if (!this.state.playable) return;
        if (this.lastSize && this.lastSize.y !== this.getSize().y) {
            const scaleUpdate = this.lastSize.y / this.getSize().y;
            const { drags } = this.state;
            this.setState({
                drags: drags.map(({ x, y }) => ({
                    x: x / scaleUpdate,
                    y: y / scaleUpdate,
                }))
            })
        }
        this.lastSize = this.getSize();
        if (this.props.isLow !== prevProps.isLow) {
            const { drags } = this.state;
            this.setState({
                drags: drags.map((drag) => {
                    return {
                        x: drag.x * (this.props.isLow ? 0.52 : 1) / (this.props.isLow ? 1 : 0.52),
                        y: drag.y * (this.props.isLow ? 0.52 : 1) / (this.props.isLow ? 1 : 0.52),
                    }
                }),
            })
        }
    }

    componentWillUnmount() {
        this.unmounting = true;
        window.removeEventListener("blur", this.blur);
        window.removeEventListener("resize", this.setScale);
        if (this.state.playing) {
            this.play();
        }
        setTimeout(() => {
            this.loaded = true;
        }, 500);
    }
    UNSAFE_componentWillReceiveProps = () => {
        if (!this.props.playPlan && !this.props.join && this.loaded) {
            setTimeout(() => {
                if (this.unmounting) return;
                this.setUp();
            });
        }
    };

    setUp = () => {
        let bundle: IBundle | null = null;
        const { task, plan, isLow, reversedToneset } = this.props;
        const score = getScore(plan, task);
        if (!score) {
            return;
        }
        let doPos = parseDo(score.doPos || { i: 0, o: 4 });
        if (score && score.bars[1] && score.bars[1].notes && score.bars[1].notes.length > 0) {
            const accidentals = getAccidentals(score);
            bundle = parseScore(score, accidentals, true) as IBundle;
            if (!bundle) {
                return this.setState({ bars: false });
            }
        } else {
            return;
        }
        let scale = bundle!.scale;
        if (reversedToneset) {
            scale = scale.reverse();
        }
        const barAccidentals: IBarAccidental[] = [];
        const bars = [scale.map(tone => {
            if (tone.a === 1 || tone.a === -1) {
                let alreadyHasToneIndex = barAccidentals.findIndex(a => a.fullTone === tone.tone);
                if (alreadyHasToneIndex !== -1) {
                    barAccidentals.splice(alreadyHasToneIndex, 1);
                }
                if (tone.a === 1) {
                    barAccidentals.push({
                        fullTone: tone.tone,
                        a: 1,
                    });
                } else if (tone.a === -1) {
                    barAccidentals.push({
                        fullTone: tone.tone,
                        a: -1,
                    });
                }
            }
            let modifierWidth = 0;
            if (tone.a === 1 || tone.a === 0 && barAccidentals.findIndex(a => a.fullTone === tone.tone) !== -1) {
                modifierWidth = 1;
            } else if (tone.a === -1) {
                const accidental = getAccidental({ _do: doPos });
                modifierWidth = tone.pitch.s + accidental < 0 ? 2 : 1
            }
            return ({
                l: 4,
                p: tone.tone,
                a: tone.a,
                d: undefined,
                t: undefined,
                tie: undefined,
                s: tone.s,
                o: tone.o,
                pitch: tone.pitch,
                modifierWidth,
            }) as IMelodyTableNote
        })];
        const lowDim = isLow ? 0.52 : 1;
        const drags = getRandomDragPositions(this.getDropParams(bars, doPos), this.getSize(bars), lowDim);
        this.initialDragPositions = [...drags];
        const zIndexes = drags.map(() => 11);
        const tonesetSolutions = drags.map(() => null);
        const width = bars[0].length;
        const incorrects: number[] = [];
        if (!this.props.preview) loadPiano(scale, false, false);

        this.setState({
            bars,
            scale,
            width,
            incorrects,
            doPos,
            completed: false,
            submited: false,
            submitable: false,
            drags,
            tonesetSolutions,
            zIndexes,
            playable: true,
        });
    }

    checkReady = () => {
        const bars = this.state.bars as IMelodyTableNote[][];
        let ready = true;
        bars[0].forEach((note, noteIndex) => {
            if (note && Object.keys(note).length === 0 && bars[0][noteIndex].p !== null) {
                ready = false;
            }
        });
        if (ready) {
            this.setState({ submitable: true });
        } else {
            this.setState({ submitable: false });
        }
    };

    submit = () => {
        const { tonesetSolutions } = this.state;
        const completed = tonesetSolutions.filter((solution, index) => solution === index).length === tonesetSolutions.length
        const incorrects = tonesetSolutions.filter((solution, index) => solution !== index) as number[];
        if (completed) {
            if (this.props.completed) {
                this.props.completed("tonesetStaff");
            }
        } else {
            this.props.handleFailCount!();
        }

        this.setState({ incorrects, submited: true, completed });
    };

    blur = () => {
        this.play();
    };

    play = () => {
        const { scale, playing } = this.state;
        const bars = this.state.bars as IMelodyTableNote[][];
        if (playing) {
            window.removeEventListener("blur", this.blur);
            this.setState({ playing: false, playingProps: {} });
            this.clock.stop();
            piano.stop();
            delete this.clock;
            window.cancelAnimationFrame(this.playScrollAnimation!);
            return;
        }
        window.addEventListener("blur", this.blur);
        const notes = [];
        let t = 0;
        let late = 0.2;
        this.clock = new Clock(player.context, { toleranceEarly: 0.1 });
        this.clock.start();
        this.props.handlePlayCount!();
        const { plan, task } = this.props;
        let tempo = getTempo(plan, task);
        let lastNote;
        for (let b = 0; b < bars.length; b++) {
            let bar = bars[b];
            for (let n = 0; n < bar.length; n++) {
                let note = bar[n];
                let noteLength = (4 / note.l) * (60 / tempo) * (note.d ? 1.5 : 1) * (note.t ? 0.67 : 1);
                let prolong = 0;
                if (note.tie) {
                    let nextNote = bar[n + 1];
                    if (!nextNote) {
                        nextNote = bars[b + 1][0];
                    }
                    prolong = (4 / nextNote.l) * (60 / tempo) * (nextNote.d ? 1.5 : 1) * (nextNote.t ? 0.67 : 1);
                }
                if (note.p !== null && (!lastNote || !lastNote.tie)) {
                    notes.push({
                        time: t,
                        note: scale[scale.findIndex(tone => tone.tone === note.p && tone.a === note.a)].piano,
                        duration: noteLength + prolong,
                    });
                }
                let event = this.clock.callbackAtTime(() => {
                    this.setState({
                        playingProps: {
                            bar: b,
                            note: n,
                        },
                    });
                }, player.context.currentTime + t + late);
                event.tolerance({ late: 0.01 });
                lastNote = note;
                t += noteLength;
            }
        }
        this.scroll!.scrollLeft = 0;
        let scrollWidth = this.scroll!.getBoundingClientRect().width;
        let frameScale = 1280 / scrollWidth;
        let scrollToX =
            (this.wrap!.getBoundingClientRect().width - scrollWidth) * frameScale * (this.props.isLow ? 666 / 1280 : 1);
        scrollTo(this.scroll!, scrollToX, t * 1000, t => (this.playScrollAnimation = t));
        let event = this.clock.callbackAtTime(this.play, player.context.currentTime + t + late);
        event.tolerance({ late: 0.01 });

        piano.schedule(player.context.currentTime + late, notes);
        this.setState({ playing: true });
    };

    getSize = (_bars?: IMelodyTableNote[][]) => {
        const { task } = this.props;
        const bars = (_bars || this.state.bars) as IMelodyTableNote[][];
        const { data } = task;
        let size: IMelodyTableData["size"];
        if (data && data.size) {
            size = {
                y: data.size.y,
                x: data.size.y * 0.6,
            };
        }
        else {
            const y = 102 - (Math.max(4, bars[0].length) - 4) * 6;
            size = { y, x: y * 0.6 };
        }
        return size;
    }
    getTableWidth = () => {
        const { width, doPos } = this.state;
        const { plan, task, isLow } = this.props;
        const notes = (this.state.bars as IMelodyTableNote[][])[0];
        const size = this.getSize();
        const score = getScore(plan, task)!;
        const clef = score.doClef ? "doClef" : (score.clef || "treble");
        const accidental = (score.doClef || score.hideClefs) ? 0 : getAccidental({ _do: doPos });
        const lowDim = isLow ? 0.52 : 1;
        const notesWidth = (width + 1) * size.x +
            (notes.length - 1) * spaceDim * size.y +
            notes.reduce((total, nextNote) => total + nextNote.modifierWidth!, 0) * size.y * modifierDim;
        const totalWidth = (notesWidth +
            (score.hideClefs ? 0 : (clefWidth[clef] * (size.y / 100))) -
            1.5 +
            40 + // accidentals margin
            Math.abs(accidental) * 95 * (size.y / 100)
        ) * lowDim;
        return {
            totalWidth,
            notesWidth,
        }
    }

    getDropParams = (_bars?: IMelodyTableNote[][], _doPos?: IParsedDo) => {
        const size = this.getSize(_bars);
        const { plan, task, isLow } = this.props;
        const { doPos } = this.state;
        const score = getScore(plan, task)!;
        const clef = score.clef || "treble";
        const accidental = getAccidental({ _do: _doPos || doPos });
        const bars = (_bars || this.state.bars) as IMelodyTableNote[][];
        let position = (score.hideClefs ? 0 : (clefWidth[score.doClef ? "doClef" : clef] * (size.y / 100))) +
            2 * (size.y / 100) +
            40 + // accidentals margin
            Math.abs(score.doClef ? 0 : accidental) * 95 * (size.y / 100)
            + size.x / 2
        const lowDim = isLow ? 0.52 : 1;
        let modifierExtra = 0;
        return {
            drops: bars[0].map((note, index) => {
                let modifierWidth = note.modifierWidth!;
                modifierExtra += modifierWidth;
                return ({
                    x: (position + index * size.x + (index) * size.y * spaceDim + modifierExtra * size.y * modifierDim) * lowDim,
                    y: (((-note.p! + 4 - clefOffset[clef]) * size.y) / 2 - size.y / 4) *
                        lowDim
                });
            }),
            width: (size.x - 4) * lowDim,
            height: (size.y / 2 - 4) * lowDim,
        };
    }

    handleDrag = (index: number, x: number, y: number) => {
        const { drags } = this.state;
        const dropParams = this.getDropParams();
        const { task, isLow } = this.props;
        const { data } = task;
        const { position = 0 } = data;
        const lowDim = isLow ? 0.52 : 1;
        const topPadding = -(ar.height - (this.getSize().y * 4)) / 2 * lowDim;
        const maxY = (this.scroll?.scrollHeight || (ar.height * 1.1)) + topPadding - position * lowDim - dropParams.height * 1.6;
        const minY = (topPadding / lowDim - position + dropParams.height / 1.5) * lowDim;
        this.setState({
            drags: drags.map((drag, dragIndex) => {
                if (dragIndex === index) {
                    return {
                        x: Math.min(Math.max(x, 0), this.getTableWidth().totalWidth),
                        y: Math.min(maxY, Math.max(y, minY)),
                    }
                }
                return drag;
            })
        })
    }

    setScale = () => {
        clearTimeout(this.timer);
        if (this.props.edit) {
            this.setState({ submitable: false });
        }
        this.timer = setTimeout(() => {
            if (!this.frame?.children?.[0]) return;
            let frameWidth = this.frame.children[0].getBoundingClientRect().width;
            let frameScale = frameWidth / ar.width;
            this.setState({ frameScale });
        }, 550);
    };

    handleDragEnd = (index: number) => {
        const { drags, tonesetSolutions } = this.state;
        const drag = drags[index];
        const { drops } = this.getDropParams();
        let dropTarget = isNearAnyDrop(drops, drag.x, drag.y, 45);
        if (dropTarget !== null) {
            this.setState({ transition: true }, () => {
                const oldSolution = tonesetSolutions[dropTarget!];
                const newTonesetSolutions = tonesetSolutions.map((solution, solutionIndex) => {
                    if (dropTarget === solutionIndex) {
                        return index;
                    }
                    return solution;
                });
                this.setState({
                    drags: drags.map((drag, dragIndex) => {
                        if (dragIndex === index) {
                            return drops[dropTarget!]
                        }
                        if (dragIndex === oldSolution) {
                            return this.initialDragPositions![dragIndex];
                        }
                        return drag;
                    }),
                    tonesetSolutions: newTonesetSolutions,
                    submitable: newTonesetSolutions.filter(solution => solution === null).length === 0
                });
                setTimeout(() => {
                    this.setState({ transition: false });
                }, 400);
            });
        }
    }

    handeDragStart = (dragIndex: number) => {
        const { tonesetSolutions, zIndexes } = this.state;
        const newTonesetSolutions = tonesetSolutions.map(solution => {
            if (solution === dragIndex) {
                return null;
            }
            return solution;
        })
        this.setState({
            tonesetSolutions: newTonesetSolutions,
            submited: false,
            submitable: newTonesetSolutions.filter(solution => solution === null).length === 0,
            zIndexes: zIndexes.map((zIndex, index) => {
                if (dragIndex === index) {
                    return Math.max.apply(null, zIndexes) + 1;
                }
                return zIndex;
            })
        });
    }

    handleStart = (dragIndex: number) => {
        const { plan, task, intl, solfaAudio } = this.props;
        const { scale, playing, doPos } = this.state;
        if (playing) return;
        const bars = this.state.bars as IMelodyTableNote[][];
        const note = bars[0][dragIndex];
        const score = getScore(plan, task)!;
        const { method = TonesetText.Solfa } = task.data;
        const pianoNote = scale[scale.findIndex(tone => tone.tone === note.p && tone.a === note.a)].piano;
        if (method === TonesetText.Solfa || method === TonesetText.Handsign) {
            const playNote = {
                pianoNote,
                doPos: score.doPos,
                p: note.pitch,
                octave: 0,
                locale: intl.locale,
                clef: score.clef || "treble",
                isLala: false,
                type: solfaAudio,
            };
            let loaded = voices.load(playNote);
            if (loaded) {
                voices.play(playNote);
            }
        } else if (method === TonesetText.Empty) {
            const singingNote = {
                p: {} as IPitch,
                pianoNote,
                isLala: true,
                doPos: {
                    i: 0,
                    o: 1,
                } as IDoPos,
                clef: score.clef || "treble",
                type: solfaAudio,
                locale: "en",
                octave: 0,
            };
            let loaded = voices.load(singingNote);
            if (loaded) {
                voices.play(singingNote);
            }
        } else {
            const keyOffset = getKeyOffset(doPos.value + 6) + doPos.alt;
            let pianoNote =
                60 + pianoParser[note.pitch.s] + (note.pitch.a || 0) + keyOffset + (note.pitch.o || 0 + score.octave0 || 0) * 12;
            let loaded = piano.load(pianoNote, true, { stop: 0.6 });
            if (loaded) {
                piano.play(pianoNote, player.context.currentTime, 0.6);
            }
        }
    }

    getDoPosition = () => {
        const { task, plan } = this.props;
        const score = getScore(plan, task)! as IScore;
        let doPosition = 0;
        const clef = score.clef || "treble";
        const clefType: "treble" | "bass" = (clef === "treble-octave" ? "treble" : clef);
        if (score.doClef) {
            const offset = (score.doClefOffset || [])[0] || 0;
            doPosition = getDoPosition(clefType, score, 0) + 4 - offset + clefCorrection[clefType];
        }
        return doPosition;
    }

    render() {
        const {
            bars,
            incorrects,
            submitable,
            submited,
            doPos,
            playing,
            completed,
            playingProps,
            drags,
            frameScale,
            transition,
            zIndexes,
            tonesetSolutions,
            playable,
        } = this.state;
        const { task, isLow, isDemo, plan, handleHomeworkNext, intl: { locale }, isEaster, isChristmas } = this.props;
        const { data } = task;
        const { color } = data;
        if (!bars || !data || !playable)
            return (
                <div>
                    <div>
                        <Wrapper>
                            <NotEdited />
                        </Wrapper>
                    </div>
                </div>
            );
        const score = getScore(plan, task) as IScore;
        const { colors = false } = score.settings;
        const { position = 0 } = data;
        const accidental = score.doClef ? 0 : getAccidental({ _do: doPos });
        const size = this.getSize();
        let clef = score.clef || "treble";
        const lowDim = isLow ? 0.52 : 1;
        const { totalWidth: tableWidth, notesWidth } = this.getTableWidth();
        const dropParams = this.getDropParams();
        const barAccidentals: IBarAccidental[] = [];
        const doPosition = this.getDoPosition();
        return (
            <ColorsProvider>
                <div ref={e => (this.frame = e!)}>
                    {!!bars && (
                        <Wrapper isDemo={isDemo}>
                            {isLow && (
                                <>
                                    <Asset1Wrapper>
                                        <img src={getBirdsPath("/tonesetStaff/asset-01.svg")} alt="decoration" />
                                    </Asset1Wrapper>
                                </>
                            )}
                            <ScrollWrap isLow={isLow}>
                                <Scroll ref={s => (this.scroll = s)}>
                                    <Wrap
                                        style={{
                                            width: tableWidth + 50
                                        }}
                                        ref={w => (this.wrap = w)}
                                    >
                                        <StaffLines
                                            style={{
                                                transform: `translateY(${position * lowDim || 0}px)`,
                                            }}
                                        >
                                            {[0, 1, 2, 3, 4].map(line => {
                                                return (
                                                    <StaffLine
                                                        key={line}
                                                        isChristmas={isChristmas}
                                                        style={{
                                                            marginTop: -((size.y / 25) * lowDim * 1.75),
                                                            marginBottom: line < 4 ? size.y * lowDim : 0,
                                                            height: (size.y / 25) * lowDim * 2,
                                                        }}
                                                    ></StaffLine>
                                                );
                                            })}
                                            <System>
                                                {!score.hideClefs && !score.doClef && (
                                                    <Clef style={{ top: size.y * lowDim * 3 }} isLow={isLow} isChristmas={isChristmas}>
                                                        {(clef === "treble" || clef === "treble-octave") && (
                                                            <ClefViolin
                                                                height={((840 * size.y) / 120) * lowDim}
                                                                marginTop={((-550 * size.y) / 120) * lowDim}
                                                            />
                                                        )}
                                                        {clef === "bass" && (
                                                            <ClefBass
                                                                height={((420 * size.y) / 120) * lowDim}
                                                                marginTop={((-372 * size.y) / 120) * lowDim}
                                                            />
                                                        )}
                                                        {clef === "treble-octave" && (
                                                            <OctaveMark
                                                                style={{
                                                                    top: (278 * size.y) / 120,
                                                                    fontSize: (100 * size.y) / 120,
                                                                    left: (96 * size.y) / 120,
                                                                }}
                                                            >
                                                                <i className="s-number_8" />
                                                            </OctaveMark>
                                                        )}
                                                    </Clef>
                                                )}
                                                {!score.hideClefs && score.doClef && (
                                                    <>
                                                        <ClefLedgerHolder>
                                                            {[-6, -4, -2, 10, 12, 14].map(line => {
                                                                const clefType: "treble" | "bass" = (clef === "treble-octave" ? "treble" : clef);
                                                                const position = doPosition;
                                                                if ((position <= line && line < 0) || (position >= line && line > 0)) {
                                                                    return (
                                                                        <ClefLedger
                                                                            style={{
                                                                                top: (-clefTop[clefType] - 4 / 2 + clefCorrection[clefType] / 2) * size.y + (line - 4) * (size.y / 2),
                                                                                left: 0,
                                                                                width: size.y * 1.92,
                                                                                height: size.y / 25 * 2,
                                                                                marginTop: -size.y / 25,
                                                                            }}
                                                                        />
                                                                    )
                                                                }
                                                                return null;
                                                            })}
                                                        </ClefLedgerHolder>
                                                        <DoClef
                                                            style={{
                                                                top: doPosition * size.y / 2,
                                                                fontSize: size.y * 1.75,
                                                                marginTop: -size.y * 1.28,
                                                                marginRight: 18.4 * (size.y / 100)
                                                            }}
                                                        >
                                                            {getDo(locale)}
                                                        </DoClef>
                                                    </>
                                                )}
                                                <Accidentals style={{
                                                    marginLeft: 20 * lowDim,
                                                    marginRight: 20 * lowDim,
                                                }}>
                                                    {!score.hideClefs && accidental > 0 &&
                                                        [1, 2, 3, 4, 5, 6, 7].map(acc => {
                                                            if (acc > accidental) return null;
                                                            return (
                                                                <Accidental
                                                                    key={acc}
                                                                    style={{
                                                                        top:
                                                                            (((sharpPosition[clef][acc]! + 4) * size.y) /
                                                                                2) *
                                                                            lowDim,
                                                                    }}
                                                                >
                                                                    <span
                                                                        className="s-sharp"
                                                                        style={{
                                                                            fontSize: ((290 * size.y) / 106) * lowDim,
                                                                            transform: `translateY(${((-160 * size.y) / 112) * lowDim
                                                                                }px)`,
                                                                        }}
                                                                    />
                                                                </Accidental>
                                                            );
                                                        })
                                                    }
                                                    {!score.hideClefs && accidental < 0 &&
                                                        [1, 2, 3, 4, 5, 6, 7].map(acc => {
                                                            if (-acc < accidental) return null;
                                                            return (
                                                                <Accidental
                                                                    key={acc}
                                                                    style={{
                                                                        top:
                                                                            (((flatPosition[clef][acc]! + 4) * size.y) / 2) *
                                                                            lowDim,
                                                                    }}
                                                                >
                                                                    <span
                                                                        className="s-flat"
                                                                        style={{
                                                                            fontSize: ((290 * size.y) / 106) * lowDim,
                                                                            transform: `translateY(${((-160 * size.y) / 120) * lowDim
                                                                                }px)`,
                                                                        }}
                                                                    />
                                                                </Accidental>
                                                            );
                                                        })}
                                                </Accidentals>
                                                <div style={{ width: notesWidth * lowDim }}></div>
                                                <DragHolder style={{ left: Math.max(0, (1280 * lowDim - tableWidth - this.scrollBarWidth!) / 2) }}>
                                                    {dropParams.drops.map((drop, dropIndex) => {
                                                        const note = bars[0][dropIndex]
                                                        if (note.a === 1 || note.a === -1) {
                                                            let alreadyHasToneIndex = barAccidentals.findIndex(a => a.fullTone === note.p);
                                                            if (alreadyHasToneIndex !== -1) {
                                                                barAccidentals.splice(alreadyHasToneIndex, 1);
                                                            }
                                                            if (note.a === 1) {
                                                                barAccidentals.push({
                                                                    fullTone: note.p!,
                                                                    a: 1,
                                                                });
                                                            } else if (note.a === -1) {
                                                                barAccidentals.push({
                                                                    fullTone: note.p!,
                                                                    a: -1,
                                                                });
                                                            }
                                                        }
                                                        return (
                                                            <Fragment key={dropIndex}>
                                                                {[-6, -8, -10, -12].map(line => {
                                                                    if (bars[0][dropIndex].p! <= line + ledgerOffset[clef]) {
                                                                        return (
                                                                            <Ledger
                                                                                key={`${dropIndex}_${line}`}
                                                                                style={{
                                                                                    width: dropParams.width + size.y / 10,
                                                                                    height: size.y / 35 * lowDim,
                                                                                    left: drop.x - size.y / 20,
                                                                                    top: (size.y * (-line / 2 + 2) - size.y / 70 )* lowDim,
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                    return null;
                                                                })}
                                                                {[6, 8, 10, 12].map(line => {
                                                                    if (bars[0][dropIndex].p! >= line + ledgerOffset[clef]) {
                                                                        return (
                                                                            <Ledger
                                                                                key={`${dropIndex}_${line}`}
                                                                                style={{
                                                                                    width: dropParams.width + size.y / 10,
                                                                                    height: size.y / 35,
                                                                                    left: drop.x - size.y / 20,
                                                                                    top: size.y * (-line / 2 + 2) - size.y / 70,
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                    return null;
                                                                })}
                                                                <Drop
                                                                    className={cn({ Playing: playingProps.note! === dropIndex })}
                                                                    style={{
                                                                        width: dropParams.width,
                                                                        height: dropParams.height,
                                                                        left: drop.x,
                                                                        top: drop.y,
                                                                        ...(data.method === TonesetText.Handsign && tonesetSolutions[dropIndex] !== null && {
                                                                            backgroundColor: "transparent",
                                                                        }),
                                                                    }}
                                                                >
                                                                    {note.a === 1 && (
                                                                        <Modifier
                                                                            style={{
                                                                                fontSize: size.y * 1.4 * lowDim,
                                                                                top: -size.y * 0.57 * lowDim,
                                                                                left: -size.y * 0.5 * lowDim,
                                                                            }}
                                                                        >
                                                                            {note.pitch.s + accidental >= 7
                                                                                ? <span className="s-double_sharp" />
                                                                                : note.pitch.s + accidental < 0
                                                                                    ? <span className="s-natural" />
                                                                                    : <span className="s-sharp" />
                                                                            }
                                                                        </Modifier>
                                                                    )}
                                                                    {note.a === -1 && (
                                                                        <Modifier
                                                                            style={{
                                                                                fontSize: size.y * 1.4 * lowDim,
                                                                                top: -size.y * 0.57 * lowDim,
                                                                                left: -size.y * 0.5 * (note.pitch.s + accidental < 0 ? 2 : 1) * lowDim,
                                                                            }}
                                                                        >
                                                                            {note.pitch.s + accidental >= 7
                                                                                ? <span className="s-natural" />
                                                                                : note.pitch.s + accidental >= 0
                                                                                    ? <span className="s-flat" />
                                                                                    : <span className="s-double_flat" />
                                                                            }
                                                                        </Modifier>
                                                                    )}
                                                                    {note.a === 0 &&
                                                                        barAccidentals.findIndex(a => a.fullTone === note.p) !== -1 && (
                                                                            <Modifier
                                                                                style={{
                                                                                    fontSize: size.y * 1.4 * lowDim,
                                                                                    top: -size.y * 0.57 * lowDim,
                                                                                    left: -size.y * 0.5 * lowDim,
                                                                                }}
                                                                            >
                                                                                {note.pitch.s + accidental >= 7
                                                                                    ? <span className="s-sharp" />
                                                                                    : note.pitch.s + accidental < 0
                                                                                        ? <span className="s-flat" />
                                                                                        : <span className="s-natural" />
                                                                                }
                                                                            </Modifier>
                                                                        )}
                                                                </Drop>
                                                            </Fragment>
                                                        );
                                                    })}

                                                    {drags.map((drag, dragIndex) => (
                                                        <Draggable
                                                            key={dragIndex}
                                                            onStop={() => this.handleDragEnd(dragIndex)}
                                                            onClick={() => null}
                                                            onStart={() => this.handleStart(dragIndex)}
                                                            onDrag={(x: number, y: number) => this.handleDrag(dragIndex, x, y)}
                                                            onDragStart={() => this.handeDragStart(dragIndex)}
                                                            position={drag}
                                                            disabled={completed}
                                                            scale={frameScale}
                                                            noBoundry
                                                            transition={transition}
                                                            zIndex={zIndexes[dragIndex]}
                                                        >
                                                            <Drag
                                                                style={{
                                                                    width: dropParams.width,
                                                                    height: dropParams.height,
                                                                    ...(!colors && { backgroundColor: convertToNewColors(color) }),
                                                                    fontSize: 20 * (size.y / 100) * lowDim,
                                                                    ...(data.method === TonesetText.Handsign && {
                                                                        backgroundColor: "transparent",
                                                                        boxShadow: "none",
                                                                    })
                                                                }}
                                                                className={cn({
                                                                    Incorrect: data.method !== TonesetText.Handsign && submited && incorrects.indexOf(dragIndex) !== -1,
                                                                    Correct: data.method !== TonesetText.Handsign && submited && incorrects.indexOf(dragIndex) === -1,
                                                                })}
                                                                solfaShort={colors && getSolfaName(bars[0][dragIndex].pitch!, "hu").slice(0, 1)}
                                                                coloring={colors}
                                                            >
                                                                <Text
                                                                    data={{
                                                                        note: bars[0][dragIndex],
                                                                        locale,
                                                                        method: data.method || TonesetText.Solfa,
                                                                        doPos: score.doPos,
                                                                        colors,
                                                                        size,
                                                                        color: convertToNewColors(color).slice(1),
                                                                        lowDim,
                                                                        className: cn({
                                                                            Incorrect: submited && incorrects.indexOf(dragIndex) !== -1,
                                                                            Correct: submited && incorrects.indexOf(dragIndex) === -1,
                                                                        }),
                                                                        octave0: score.octave0,
                                                                    }}
                                                                />
                                                            </Drag>
                                                        </Draggable>
                                                    ))}
                                                </DragHolder>
                                            </System>
                                        </StaffLines>
                                    </Wrap>
                                </Scroll>
                            </ScrollWrap>
                            <PlayBtn
                                isEaster={isEaster}
                                isLow={!!isLow}
                                className={cn("button", "button--play")}
                                onClick={() => this.play()}
                            >
                                {playing ? <Icon i="stop" /> : <Icon i="play" />}
                                <Label>
                                    <FormattedMessage {...messages.play} />
                                </Label>
                            </PlayBtn>
                            {submitable && !submited && !completed && (
                                <SubmitButton
                                    isEaster={isEaster}
                                    isLow={!!isLow}
                                    style={{ top: isLow ? 30 : 6, bottom: "auto" }}
                                    className={cn("button")}
                                    onClick={() => {
                                        if (submitable) {
                                            this.submit();
                                        }
                                    }}
                                >
                                    <FormattedMessage {...messages.submit} />
                                </SubmitButton>
                            )}
                            {completed && (
                                <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="completed" handleHomeworkNext={handleHomeworkNext} />
                            )}
                            {submited && !completed && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="incorrect" />}
                        </Wrapper>
                    )}
                </div>
            </ColorsProvider>
        );
    }
}

export default TonesetStaff;
